/**
 * 标准相切配合求解器。
 *
 * 文件职责：
 * 1. 独立处理 Tangent Mate 的几何组合；
 * 2. 只消费可序列化实体描述符与组件 transform；
 * 3. 避免主 StandardMateSolver 承载过多具体配合类型细节。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../component-instance';
import {
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import type { AssemblyMateSolverComponentState } from './StandardMateSolverTypes';
import {
  applyWorldDeltaToTransform,
  createRotationBetweenDirections,
  createRotationDeltaAroundPoint,
  createTranslationDelta,
  normalizeVector,
  resolveAxisGeometry,
  resolveConeGeometry,
  resolvePlaneGeometry,
  resolveSphereGeometry,
  type ConeGeometry,
  type AxisGeometry,
  type PlaneGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  isRoundRoundTangentInternalBranchDegenerate,
  resolveAlignedPlaneRoundTangentDistance,
  resolveAlignedRoundRoundTangentDistance,
} from './geometry/StandardMateTangentAlignment';
import {
  resolveAxisTangentRadius,
  resolveConeSphereSignedDistance,
} from './geometry/StandardMateTangentGeometry';

/**
 * 相切配合移动侧输入。
 */
export interface StandardMateTangentMovingSide {
  /** 固定侧实体。 */
  readonly fixedEntity: MateEntityRef;
  /** 移动侧实体。 */
  readonly movingEntity: MateEntityRef;
  /** 移动侧组件。 */
  readonly movingComponent: AssemblyMateSolverComponentState;
  /** 是否移动 Mate 的第一选择实体。 */
  readonly movingFirst: boolean;
}

/**
 * 相切配合应用结果。
 */
export interface StandardMateTangentApplyResult {
  /** 求解状态。 */
  readonly status: MateSolveStatus;
  /** 诊断信息。 */
  readonly message?: string;
  /** 移动组件 ID。 */
  readonly movedComponentInstanceId?: string;
  /** 移动组件求解后 transform。 */
  readonly transform?: ComponentTransformSnapshot;
}

/**
 * 将点投影到轴线上。
 *
 * @param point 待投影点。
 * @param axis 目标轴线。
 * @returns 投影点。
 */
function projectPointToAxis(point: THREE.Vector3, axis: AxisGeometry): THREE.Vector3 {
  const originToPoint = point.clone().sub(axis.origin);
  return axis.origin.clone().addScaledVector(axis.direction, originToPoint.dot(axis.direction));
}

/**
 * 为方向构造稳定的垂直方向。
 *
 * @param direction 源方向。
 * @param epsilon 容差。
 * @returns 垂直方向。
 */
function resolvePerpendicularDirection(direction: THREE.Vector3, epsilon: number): THREE.Vector3 {
  const seed = Math.abs(direction.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const perpendicular = direction.clone().cross(seed);
  return normalizeVector(perpendicular, epsilon) ?? new THREE.Vector3(0, 0, 1);
}

/**
 * 根据当前有符号距离选择最近的相切侧。
 *
 * @param signedDistance 当前有符号距离。
 * @param targetDistance 无符号目标距离。
 * @returns 有符号目标距离。
 */
function resolveSignedTangentDistance(signedDistance: number, targetDistance: number): number {
  return signedDistance < 0 ? -targetDistance : targetDistance;
}

/**
 * 读取平面相切需要的圆形半径。
 *
 * @param axis 轴线几何。
 * @returns 半径；缺少半径时返回 null。
 */
function resolveRoundAxisRadius(axis: AxisGeometry): number | null {
  return typeof axis.radius === 'number' && Number.isFinite(axis.radius)
    ? axis.radius
    : null;
}

/**
 * 创建点移动到距固定轴指定距离的平移量。
 *
 * @param movingPoint 被移动点。
 * @param fixedAxis 固定轴线。
 * @param targetDistance 目标距离。
 * @param epsilon 容差。
 * @returns 平移量。
 */
function createPointToAxisDistanceTranslation(
  movingPoint: THREE.Vector3,
  fixedAxis: AxisGeometry,
  targetDistance: number,
  epsilon: number,
): THREE.Vector3 {
  const projection = projectPointToAxis(movingPoint, fixedAxis);
  const radial = movingPoint.clone().sub(projection);
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolvePerpendicularDirection(fixedAxis.direction, epsilon);
  const targetPoint = projection.addScaledVector(radialDirection, targetDistance);
  return targetPoint.sub(movingPoint);
}

/**
 * 创建轴线移动到距固定点指定距离的平移量。
 *
 * @param movingAxis 被移动轴线。
 * @param fixedPoint 固定点。
 * @param targetDistance 目标距离。
 * @param epsilon 容差。
 * @returns 平移量。
 */
function createAxisToPointDistanceTranslation(
  movingAxis: AxisGeometry,
  fixedPoint: THREE.Vector3,
  targetDistance: number,
  epsilon: number,
): THREE.Vector3 {
  const projection = projectPointToAxis(fixedPoint, movingAxis);
  const radial = projection.clone().sub(fixedPoint);
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolvePerpendicularDirection(movingAxis.direction, epsilon);
  const targetProjection = fixedPoint.clone().addScaledVector(radialDirection, targetDistance);
  return targetProjection.sub(projection);
}

/**
 * 创建点移动到距固定点指定距离的平移量。
 *
 * @param movingPoint 被移动点。
 * @param fixedPoint 固定点。
 * @param targetDistance 目标距离。
 * @param epsilon 容差。
 * @returns 平移量。
 */
function createPointToPointDistanceTranslation(
  movingPoint: THREE.Vector3,
  fixedPoint: THREE.Vector3,
  targetDistance: number,
  epsilon: number,
): THREE.Vector3 {
  const radial = movingPoint.clone().sub(fixedPoint);
  const radialDirection = normalizeVector(radial, epsilon) ?? new THREE.Vector3(1, 0, 0);
  return fixedPoint.clone().addScaledVector(radialDirection, targetDistance).sub(movingPoint);
}

/**
 * 创建相切平移求解结果。
 *
 * @param side 移动侧输入。
 * @param delta 平移量。
 * @returns 应用结果。
 */
function createTangentTranslationResult(
  side: StandardMateTangentMovingSide,
  delta: THREE.Vector3,
): StandardMateTangentApplyResult {
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(side.movingComponent.transform, createTranslationDelta(delta)),
  };
}

/**
 * 解析两个圆形实体之间的 Tangent 目标距离。
 *
 * @param mate 当前 Tangent Mate 定义。
 * @param firstRadius 第一个圆形实体半径。
 * @param secondRadius 第二个圆形实体半径。
 * @param epsilon 长度容差。
 * @returns 可求解目标距离；退化为重合时返回 null。
 */
function resolveRoundRoundTangentTargetDistance(
  mate: AssemblyMateDefinition,
  firstRadius: number,
  secondRadius: number,
  epsilon: number,
): number | null {
  if (isRoundRoundTangentInternalBranchDegenerate(mate, firstRadius, secondRadius, epsilon)) {
    return null;
  }
  return resolveAlignedRoundRoundTangentDistance(mate, firstRadius, secondRadius);
}

/**
 * 创建圆形实体内切退化诊断。
 *
 * @returns 未解求解结果。
 */
function createDegenerateRoundRoundTangentResult(): StandardMateTangentApplyResult {
  return {
    status: MateSolveStatus.Unsolved,
    message: '等半径圆形实体的反向相切会退化为重合，无法创建有效相切配合。',
  };
}

/**
 * 为方向构造稳定的垂直方向。
 *
 * @param direction 源方向。
 * @param epsilon 容差。
 * @returns 垂直方向。
 */
function resolveStablePerpendicularDirection(direction: THREE.Vector3, epsilon: number): THREE.Vector3 {
  const seed = Math.abs(direction.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const perpendicular = direction.clone().cross(seed);
  return normalizeVector(perpendicular, epsilon) ?? new THREE.Vector3(0, 0, 1);
}

/**
 * 将移动圆柱/圆边轴向旋到固定平面内。
 *
 * @param side 移动侧。
 * @param fixedPlane 固定平面。
 * @param movingAxis 移动轴线。
 * @param epsilon 容差。
 * @returns 旋转后的移动组件和轴线几何。
 */
function alignMovingAxisParallelToPlane(
  side: StandardMateTangentMovingSide,
  fixedPlane: PlaneGeometry,
  movingAxis: AxisGeometry,
  epsilon: number,
): { readonly transform: ComponentTransformSnapshot; readonly axis: AxisGeometry } | null {
  const directionDot = movingAxis.direction.dot(fixedPlane.normal);
  if (Math.abs(directionDot) <= epsilon) {
    return {
      transform: side.movingComponent.transform,
      axis: movingAxis,
    };
  }

  const projectedDirection = movingAxis.direction
    .clone()
    .sub(fixedPlane.normal.clone().multiplyScalar(directionDot));
  const targetDirection = normalizeVector(projectedDirection, epsilon)
    ?? resolveStablePerpendicularDirection(fixedPlane.normal, epsilon);
  const rotation = createRotationBetweenDirections(movingAxis.direction, targetDirection, epsilon);
  if (!rotation) {
    return null;
  }

  const rotationDelta = createRotationDeltaAroundPoint(movingAxis.origin, rotation);
  return {
    transform: applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta),
    axis: {
      ...movingAxis,
      origin: movingAxis.origin.clone().applyMatrix4(rotationDelta),
      direction: movingAxis.direction
        .clone()
        .applyMatrix3(new THREE.Matrix3().getNormalMatrix(rotationDelta))
        .normalize(),
    },
  };
}

/**
 * 将移动平面法向旋到与固定圆柱/圆边轴线垂直。
 *
 * @param side 移动侧。
 * @param fixedAxis 固定轴线。
 * @param movingPlane 移动平面。
 * @param epsilon 容差。
 * @returns 旋转后的移动组件和平面几何。
 */
function alignMovingPlaneNormalPerpendicularToAxis(
  side: StandardMateTangentMovingSide,
  fixedAxis: AxisGeometry,
  movingPlane: PlaneGeometry,
  epsilon: number,
): { readonly transform: ComponentTransformSnapshot; readonly plane: PlaneGeometry } | null {
  const normalDot = movingPlane.normal.dot(fixedAxis.direction);
  if (Math.abs(normalDot) <= epsilon) {
    return {
      transform: side.movingComponent.transform,
      plane: movingPlane,
    };
  }

  const projectedNormal = movingPlane.normal
    .clone()
    .sub(fixedAxis.direction.clone().multiplyScalar(normalDot));
  const targetNormal = normalizeVector(projectedNormal, epsilon)
    ?? resolveStablePerpendicularDirection(fixedAxis.direction, epsilon);
  const rotation = createRotationBetweenDirections(movingPlane.normal, targetNormal, epsilon);
  if (!rotation) {
    return null;
  }

  const rotationDelta = createRotationDeltaAroundPoint(movingPlane.point, rotation);
  return {
    transform: applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta),
    plane: {
      point: movingPlane.point.clone().applyMatrix4(rotationDelta),
      normal: movingPlane.normal
        .clone()
        .applyMatrix3(new THREE.Matrix3().getNormalMatrix(rotationDelta))
        .normalize(),
    },
  };
}

/**
 * 构造最接近当前方向且满足目标点积的新方向。
 *
 * @param direction 待调整方向。
 * @param normal 目标平面法向。
 * @param expectedDot 目标点积。
 * @param epsilon 容差。
 * @returns 新方向；无法稳定构造时返回 null。
 */
function resolveDirectionWithNormalDot(
  direction: THREE.Vector3,
  normal: THREE.Vector3,
  expectedDot: number,
  epsilon: number,
): THREE.Vector3 | null {
  const normalizedDirection = normalizeVector(direction, epsilon);
  const normalizedNormal = normalizeVector(normal, epsilon);
  if (!normalizedDirection || !normalizedNormal) {
    return null;
  }

  const clampedDot = THREE.MathUtils.clamp(expectedDot, -1, 1);
  const currentDot = THREE.MathUtils.clamp(normalizedDirection.dot(normalizedNormal), -1, 1);
  if (Math.abs(currentDot - clampedDot) <= epsilon) {
    return normalizedDirection;
  }

  const tangentDirection = normalizeVector(
    normalizedDirection.clone().sub(normalizedNormal.clone().multiplyScalar(currentDot)),
    epsilon,
  ) ?? resolveStablePerpendicularDirection(normalizedNormal, epsilon);
  const tangentScale = Math.sqrt(Math.max(0, 1 - clampedDot * clampedDot));
  return normalizedNormal.clone()
    .multiplyScalar(clampedDot)
    .add(tangentDirection.multiplyScalar(tangentScale))
    .normalize();
}

/**
 * 求解移动圆锥与固定平面相切。
 *
 * 设计说明：
 * 对右圆锥而言，平面与圆锥相切时平面经过顶点，且平面法向与圆锥轴向的夹角余弦等于
 * `sin(halfAngle)`。因此先围绕顶点调整圆锥轴向，再把顶点移到平面上；该路径只依赖
 * `apex/axisDirection/halfAngle`，不需要 UI 或运行时对象参与。
 *
 * @param side 移动侧。
 * @param fixedPlane 固定平面。
 * @param movingCone 移动圆锥。
 * @param epsilon 容差。
 * @returns 求解结果。
 */
function solveMovingConeToFixedPlaneTangent(
  side: StandardMateTangentMovingSide,
  fixedPlane: PlaneGeometry,
  movingCone: ConeGeometry,
  epsilon: number,
): StandardMateTangentApplyResult {
  const preferredSign = movingCone.direction.dot(fixedPlane.normal) < 0 ? -1 : 1;
  const expectedDot = preferredSign * Math.sin(movingCone.halfAngle);
  const targetDirection = resolveDirectionWithNormalDot(
    movingCone.direction,
    fixedPlane.normal,
    expectedDot,
    epsilon,
  );
  if (!targetDirection) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '圆锥轴线无法稳定旋转到与平面相切的方向。',
    };
  }

  const rotation = createRotationBetweenDirections(movingCone.direction, targetDirection, epsilon);
  if (!rotation) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '圆锥轴线无法构造相切旋转。',
    };
  }

  const rotationDelta = createRotationDeltaAroundPoint(movingCone.apex, rotation);
  const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
  const rotatedApex = movingCone.apex.clone().applyMatrix4(rotationDelta);
  const signedApexDistance = rotatedApex.clone().sub(fixedPlane.point).dot(fixedPlane.normal);
  const translation = fixedPlane.normal.clone().multiplyScalar(-signedApexDistance);
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(rotatedTransform, createTranslationDelta(translation)),
  };
}

/**
 * 求解移动平面与固定圆锥相切。
 *
 * @param side 移动侧。
 * @param fixedCone 固定圆锥。
 * @param movingPlane 移动平面。
 * @param epsilon 容差。
 * @returns 求解结果。
 */
function solveMovingPlaneToFixedConeTangent(
  side: StandardMateTangentMovingSide,
  fixedCone: ConeGeometry,
  movingPlane: PlaneGeometry,
  epsilon: number,
): StandardMateTangentApplyResult {
  const preferredSign = fixedCone.direction.dot(movingPlane.normal) < 0 ? -1 : 1;
  const expectedDot = preferredSign * Math.sin(fixedCone.halfAngle);
  const targetNormal = resolveDirectionWithNormalDot(
    movingPlane.normal,
    fixedCone.direction,
    expectedDot,
    epsilon,
  );
  if (!targetNormal) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '平面法向无法稳定旋转到与圆锥相切的方向。',
    };
  }

  const rotation = createRotationBetweenDirections(movingPlane.normal, targetNormal, epsilon);
  if (!rotation) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '平面法向无法构造圆锥相切旋转。',
    };
  }

  const rotationDelta = createRotationDeltaAroundPoint(movingPlane.point, rotation);
  const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
  const rotatedPoint = movingPlane.point.clone().applyMatrix4(rotationDelta);
  const rotatedNormal = movingPlane.normal
    .clone()
    .applyMatrix3(new THREE.Matrix3().getNormalMatrix(rotationDelta))
    .normalize();
  const signedApexDistance = fixedCone.apex.clone().sub(rotatedPoint).dot(rotatedNormal);
  const translation = rotatedNormal.clone().multiplyScalar(signedApexDistance);
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(rotatedTransform, createTranslationDelta(translation)),
  };
}

/**
 * 求解移动球到固定圆锥的相切。
 *
 * @param side 移动侧。
 * @param fixedCone 固定圆锥。
 * @param movingSphere 移动球。
 * @param epsilon 容差。
 * @returns 应用结果。
 */
function solveMovingSphereToFixedConeTangent(
  side: StandardMateTangentMovingSide,
  fixedCone: ConeGeometry,
  movingSphere: { readonly center: THREE.Vector3; readonly radius: number },
  epsilon: number,
): StandardMateTangentApplyResult {
  const distanceResult = resolveConeSphereSignedDistance(fixedCone, movingSphere.center, epsilon);
  if (!distanceResult) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '圆锥面缺少可用于圆锥-球相切的顶点或半角信息。',
    };
  }
  const targetDistance = resolveSignedTangentDistance(distanceResult.signedDistance, movingSphere.radius);
  return createTangentTranslationResult(
    side,
    distanceResult.normal.clone().multiplyScalar(targetDistance - distanceResult.signedDistance),
  );
}

/**
 * 求解移动圆锥到固定球的相切。
 *
 * @param side 移动侧。
 * @param fixedSphere 固定球。
 * @param movingCone 移动圆锥。
 * @param epsilon 容差。
 * @returns 应用结果。
 */
function solveMovingConeToFixedSphereTangent(
  side: StandardMateTangentMovingSide,
  fixedSphere: { readonly center: THREE.Vector3; readonly radius: number },
  movingCone: ConeGeometry,
  epsilon: number,
): StandardMateTangentApplyResult {
  const distanceResult = resolveConeSphereSignedDistance(movingCone, fixedSphere.center, epsilon);
  if (!distanceResult) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '圆锥面缺少可用于圆锥-球相切的顶点或半角信息。',
    };
  }
  const targetDistance = resolveSignedTangentDistance(distanceResult.signedDistance, fixedSphere.radius);
  return createTangentTranslationResult(
    side,
    distanceResult.normal.clone().multiplyScalar(distanceResult.signedDistance - targetDistance),
  );
}

/**
 * 求解切线配合。
 *
 * @param side 移动侧。
 * @param epsilon 容差。
 * @returns 应用结果。
 */
export function solveTangentMate(
  mate: AssemblyMateDefinition,
  side: StandardMateTangentMovingSide,
  epsilon: number,
): StandardMateTangentApplyResult {
  const fixedPlane = resolvePlaneGeometry(side.fixedEntity);
  const movingPlane = resolvePlaneGeometry(side.movingEntity);
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  const fixedCone = resolveConeGeometry(side.fixedEntity);
  const movingCone = resolveConeGeometry(side.movingEntity);
  const fixedSphere = resolveSphereGeometry(side.fixedEntity);
  const movingSphere = resolveSphereGeometry(side.movingEntity);

  if (fixedPlane && movingCone) {
    return solveMovingConeToFixedPlaneTangent(side, fixedPlane, movingCone, epsilon);
  }

  if (fixedCone && movingPlane) {
    return solveMovingPlaneToFixedConeTangent(side, fixedCone, movingPlane, epsilon);
  }

  if (fixedCone && movingSphere) {
    return solveMovingSphereToFixedConeTangent(side, fixedCone, movingSphere, epsilon);
  }

  if (fixedSphere && movingCone) {
    return solveMovingConeToFixedSphereTangent(side, fixedSphere, movingCone, epsilon);
  }

  if (fixedPlane && movingAxis) {
    const movingRadius = resolveRoundAxisRadius(movingAxis);
    if (movingRadius !== null) {
      const aligned = alignMovingAxisParallelToPlane(side, fixedPlane, movingAxis, epsilon);
      if (!aligned) {
        return {
          status: MateSolveStatus.Unsolved,
          message: '圆柱轴线无法旋转到与平面相切的方向。',
        };
      }
      const signedDistance = aligned.axis.origin.clone().sub(fixedPlane.point).dot(fixedPlane.normal);
      const targetDistance = resolveAlignedPlaneRoundTangentDistance(mate, movingRadius);
      const translation = fixedPlane.normal.clone().multiplyScalar(targetDistance - signedDistance);
      return {
        status: MateSolveStatus.Solved,
        movedComponentInstanceId: side.movingComponent.componentInstanceId,
        transform: applyWorldDeltaToTransform(aligned.transform, createTranslationDelta(translation)),
      };
    }
  }

  if (fixedAxis && movingPlane) {
    const fixedRadius = resolveRoundAxisRadius(fixedAxis);
    if (fixedRadius !== null) {
      const aligned = alignMovingPlaneNormalPerpendicularToAxis(side, fixedAxis, movingPlane, epsilon);
      if (!aligned) {
        return {
          status: MateSolveStatus.Unsolved,
          message: '平面无法旋转到与圆柱相切的方向。',
        };
      }
      const signedDistance = fixedAxis.origin.clone().sub(aligned.plane.point).dot(aligned.plane.normal);
      const targetDistance = resolveAlignedPlaneRoundTangentDistance(mate, fixedRadius);
      const translation = aligned.plane.normal.clone().multiplyScalar(signedDistance - targetDistance);
      return {
        status: MateSolveStatus.Solved,
        movedComponentInstanceId: side.movingComponent.componentInstanceId,
        transform: applyWorldDeltaToTransform(aligned.transform, createTranslationDelta(translation)),
      };
    }
  }

  if (fixedPlane && movingSphere) {
    const signedDistance = movingSphere.center.clone().sub(fixedPlane.point).dot(fixedPlane.normal);
    const targetDistance = resolveAlignedPlaneRoundTangentDistance(mate, movingSphere.radius);
    return createTangentTranslationResult(
      side,
      fixedPlane.normal.clone().multiplyScalar(targetDistance - signedDistance),
    );
  }

  if (fixedSphere && movingPlane) {
    const signedDistance = fixedSphere.center.clone().sub(movingPlane.point).dot(movingPlane.normal);
    const targetDistance = resolveAlignedPlaneRoundTangentDistance(mate, fixedSphere.radius);
    return createTangentTranslationResult(
      side,
      movingPlane.normal.clone().multiplyScalar(signedDistance - targetDistance),
    );
  }

  if (fixedAxis && movingAxis) {
    const fixedRadius = resolveAxisTangentRadius(side.fixedEntity, fixedAxis);
    const movingRadius = resolveAxisTangentRadius(side.movingEntity, movingAxis);
    if (
      fixedRadius !== null
      && movingRadius !== null
      && Math.max(Math.abs(fixedRadius), Math.abs(movingRadius)) > epsilon
    ) {
      const targetDistance = resolveRoundRoundTangentTargetDistance(mate, fixedRadius, movingRadius, epsilon);
      if (targetDistance === null) {
        return createDegenerateRoundRoundTangentResult();
      }
      return createTangentTranslationResult(
        side,
        createPointToAxisDistanceTranslation(movingAxis.origin, fixedAxis, targetDistance, epsilon),
      );
    }
  }

  if (fixedAxis && movingSphere) {
    const fixedRadius = resolveAxisTangentRadius(side.fixedEntity, fixedAxis);
    if (fixedRadius !== null) {
      const targetDistance = resolveRoundRoundTangentTargetDistance(mate, fixedRadius, movingSphere.radius, epsilon);
      if (targetDistance === null) {
        return createDegenerateRoundRoundTangentResult();
      }
      return createTangentTranslationResult(
        side,
        createPointToAxisDistanceTranslation(movingSphere.center, fixedAxis, targetDistance, epsilon),
      );
    }
  }

  if (fixedSphere && movingAxis) {
    const movingRadius = resolveAxisTangentRadius(side.movingEntity, movingAxis);
    if (movingRadius !== null) {
      const targetDistance = resolveRoundRoundTangentTargetDistance(mate, fixedSphere.radius, movingRadius, epsilon);
      if (targetDistance === null) {
        return createDegenerateRoundRoundTangentResult();
      }
      return createTangentTranslationResult(
        side,
        createAxisToPointDistanceTranslation(movingAxis, fixedSphere.center, targetDistance, epsilon),
      );
    }
  }

  if (fixedSphere && movingSphere) {
    const targetDistance = resolveRoundRoundTangentTargetDistance(mate, fixedSphere.radius, movingSphere.radius, epsilon);
    if (targetDistance === null) {
      return createDegenerateRoundRoundTangentResult();
    }
    return createTangentTranslationResult(
      side,
      createPointToPointDistanceTranslation(
        movingSphere.center,
        fixedSphere.center,
        targetDistance,
        epsilon,
      ),
    );
  }

  return {
    status: MateSolveStatus.UnsupportedGeometry,
    message: '当前相切配合缺少圆柱、圆锥或球的半径信息。',
  };
}
